// @flow
import * as React from 'react';

import type { BundledVariant, VariantDescriptor } from '../../sharedTypes';

type RequiredProps = {|
  +defaultVariantId: string | null,
  +variants: Array<BundledVariant>,
  +id: string,
|};

type InjectedProps = {|
  +selectedVariant: BundledVariant | null,
  +selectedDescriptors: Array<VariantDescriptor>,
  +updateSelectedVariant: (string) => void,
  +updateSelectedDescriptors: (VariantDescriptor) => void,
|};

function withSelectedVariant(selectionRequired: boolean = true) {
  return function<OwnProps>(
    WrappedComponent: React.AbstractComponent<{|
      ...InjectedProps,
      ...RequiredProps,
      ...OwnProps,
    |}>
  ): React.AbstractComponent<{| ...OwnProps, ...RequiredProps |}> {
    type State = {
      +selectedVariantId: string | null,
      +selectedDescriptors: Array<VariantDescriptor>,
    };

    type DefaultProps = {|
      +overrideDefaultVariantId: string | null,
    |};

    type Props = {|
      ...OwnProps,
      ...RequiredProps,
      ...DefaultProps,
    |};

    return class WithSelectedVariant extends React.Component<Props, State> {
      static defaultProps: DefaultProps = { overrideDefaultVariantId: null };

      constructor(props: Props) {
        super(props);
        this.state = {
          selectedVariantId: this.getInitialVariantId(),
          selectedDescriptors: this.getInitialDescriptors(),
        };
      }

      componentDidUpdate(prevProps: Props) {
        this.resetSelectedVariant(prevProps);
      }

      resetSelectedVariant = (prevProps: Props) => {
        const prevId = prevProps.id;
        const { id } = this.props;
        const prevOverrideDefaultVariantId = prevProps.overrideDefaultVariantId;
        const { overrideDefaultVariantId } = this.props;
        const didProductUpdate = prevId !== id;
        const didOverrideDefaultVariantIdUpdate =
          prevOverrideDefaultVariantId !== overrideDefaultVariantId &&
          overrideDefaultVariantId !== null;

        if (didProductUpdate || didOverrideDefaultVariantIdUpdate) {
          this.setState({
            selectedVariantId: this.getInitialVariantId(),
            selectedDescriptors: this.getInitialDescriptors(),
          });
        }
      };

      getInStockDefaultVariant = (defaultVariantId) => {
        const { variants } = this.props;
        const defaultVariant = this.getSelectedVariant(defaultVariantId);
        if (defaultVariant !== null && !defaultVariant.soldOut)
          return defaultVariantId;
        const inStockVariant = variants.find((variant) => {
          return !variant.soldOut;
        });

        return inStockVariant ? inStockVariant.id : defaultVariantId;
      };

      getInitialVariantId = () => {
        const {
          defaultVariantId,
          variants,
          overrideDefaultVariantId,
        } = this.props;
        if (overrideDefaultVariantId !== null) return overrideDefaultVariantId;
        if (variants.length === 1) return variants[0].id;
        if (!selectionRequired) return defaultVariantId;

        return defaultVariantId === null
          ? this.getInStockDefaultVariant(variants[0].id)
          : this.getInStockDefaultVariant(defaultVariantId);
      };

      getInitialDescriptors = () => {
        const currentVariant = this.getSelectedVariant(
          this.getInitialVariantId()
        );
        return currentVariant !== null
          ? currentVariant.variantDescriptors || []
          : [];
      };

      updateSelectedVariant = (id) => this.setState({ selectedVariantId: id });

      updateSelectedDescriptors = (newDescriptor) => {
        let newDescriptors = [newDescriptor];
        const filteredSelectedDescriptors = this.state.selectedDescriptors.filter(
          (selectedDescriptor) => {
            return selectedDescriptor.type !== newDescriptor.type;
          }
        );
        newDescriptors = filteredSelectedDescriptors.concat(newDescriptor);

        const variant = this.getSelectedVariantByDescriptors(newDescriptors);
        if (variant !== null) {
          this.setState({
            selectedDescriptors: newDescriptors,
            selectedVariantId: variant.id,
          });
        } else {
          this.setState({ selectedDescriptors: newDescriptors });
        }
      };

      getSelectedVariant = (id) => {
        const { variants } = this.props;
        if (!selectionRequired && id === null) return null;
        return variants.find((variant) => variant.id === id) || variants[0];
      };

      getSelectedVariantByDescriptors = (variantDescriptors) => {
        const { variants } = this.props;
        if (!selectionRequired && variantDescriptors.length < 0) return null;
        // If a selection is required we must return something so select the first variant in the list
        if (variantDescriptors.length < 0) return variants[0];

        // We do not want to retrieve a variant if we do not have all the required descriptors
        if (variantDescriptors.length < variants[0].variantDescriptors.length)
          return null;

        // TODO Remove ambiguity if two different variantDescriptors have same name
        // returns a list of just the descriptors from the passed variantDescriptors
        const descriptors = variantDescriptors.map((variantDescriptor) => {
          return variantDescriptor.descriptor;
        });

        // Finds the variant that has the same variantDescriptors as those passed to the function
        const selectedVariant = variants.find((variant) => {
          const filteredVariantDescriptors = variant.variantDescriptors.filter(
            (variantDescriptor) => {
              return descriptors.includes(variantDescriptor.descriptor);
            }
          );
          return filteredVariantDescriptors.length === descriptors.length;
        });

        return selectedVariant || null;
      };

      render() {
        const { selectedDescriptors } = this.state;
        const { overrideDefaultVariantId, ...restOfProps } = this.props;
        const selectedVariant = this.getSelectedVariantByDescriptors(
          selectedDescriptors
        );
        return (
          <WrappedComponent
            {...restOfProps}
            selectedVariant={selectedVariant}
            selectedDescriptors={selectedDescriptors}
            updateSelectedVariant={this.updateSelectedVariant}
            updateSelectedDescriptors={this.updateSelectedDescriptors}
          />
        );
      }
    };
  };
}

export default withSelectedVariant;
